<script setup lang="ts">
import AppBreadcrumbsDropdown from '@/shared/ui/AppBreadcrumbs/ui/AppBreadcrumbsDropdown.vue';
import type {
  Breadcrumb,
  Props,
} from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';

defineOptions({
  name: 'AppBreadcrumbs',
});

const props = withDefaults(defineProps<Props>(), {
  breadcrumbs: () => [],
  isShowSpreadButton: false,
});

const isMobile: boolean = useIsMobile();

const isShowDropdown: Ref<boolean> = ref(false);
const activeDropdownIndex: Ref<number | null> = ref(null);
const isShowFullText: Ref<boolean> = ref(false);
const isRightSide: Ref<boolean> = ref(false);

const isLastItem = (index: number): boolean => index + 1 === props.breadcrumbs.length;

const hasHiddenFullText = (breadcrumb: Breadcrumb): boolean =>
  Boolean(breadcrumb.fullText) && breadcrumb.fullText !== breadcrumb.text;

const splitBreadcrumbText = (text: string | undefined): [string, string] => {
  if (!text) return ['', ''];
  const parts = text.trim().split(' ');
  if (parts.length === 1) return ['', parts[0] ?? ''];
  return [parts.slice(0, -1).join(' '), parts[parts.length - 1] ?? ''];
};

const getBreadcrumbParts = (breadcrumb: Breadcrumb, index: number): [string, string] => {
  const text =
    isShowFullText.value && isLastItem(index)
      ? (breadcrumb.fullText ?? breadcrumb.text)
      : breadcrumb.text;
  return splitBreadcrumbText(text);
};

const getBreadcrumbComponent = (index: number) =>
  isLastItem(index) ? 'span' : resolveComponent('NuxtLink');

const getBreadcrumbClasses = (
  breadcrumb: Breadcrumb,
  index: number,
): (string | Record<string, boolean>)[] => {
  const baseClass = isLastItem(index)
    ? 'breadcrumbs__list-text'
    : 'breadcrumbs__list-link';
  return [
    baseClass,
    {
      _down: Boolean(breadcrumb?.dropdownItems?.length),
      _mobile: isMobile,
    },
  ];
};

const showDropdown = (index: number): void => {
  isShowDropdown.value = true;
  activeDropdownIndex.value = index;
};

const toggleDropdown = (index: number): void => {
  isShowDropdown.value = !isShowDropdown.value;
  activeDropdownIndex.value = index;
};

const hideDropdown = (): void => {
  isShowDropdown.value = false;
  activeDropdownIndex.value = null;
};

const handleMouseOver = (event: MouseEvent, index: number): void => {
  const target = event.target instanceof HTMLElement ? event.target : null;
  if (target?.closest('.breadcrumbs__dropdown')) return;

  const triggerEl =
    event.currentTarget instanceof HTMLElement ? event.currentTarget : target;
  if (!triggerEl) return;
  const elementCenter =
    triggerEl.getBoundingClientRect().left + triggerEl.offsetWidth / 2;
  isRightSide.value = elementCenter > window.innerWidth / 2;
  showDropdown(index);
};

const showFullText = (): void => {
  isShowFullText.value = true;
};
</script>

<template>
  <nav :class="['breadcrumbs', { _mobile: isMobile }]">
    <ol class="breadcrumbs__list">
      <li
        v-for="(breadcrumb, index) of breadcrumbs"
        :key="breadcrumb.id"
        v-click-outside="hideDropdown"
        class="breadcrumbs__list-item"
        @mouseover="!isMobile ? handleMouseOver($event, index) : null"
        @mouseleave="!isMobile ? hideDropdown() : null"
      >
        <template v-if="isLastItem(index) && breadcrumb?.dropdownItems?.length">
          <button
            type="button"
            class="breadcrumbs__list-button"
            aria-label="Показать вложенный список"
            @mousedown.prevent
            @click.stop="toggleDropdown(index)"
          >
            <template v-if="getBreadcrumbParts(breadcrumb, index)[0]">
              {{ getBreadcrumbParts(breadcrumb, index)[0] }}&nbsp;
            </template>
            <span class="breadcrumbs__nowrap">
              {{ getBreadcrumbParts(breadcrumb, index)[1] }}
              <span
                :class="[
                  'breadcrumbs__arrow _rotate',
                  {
                    _down:
                      breadcrumb?.dropdownItems?.length &&
                      isShowDropdown &&
                      activeDropdownIndex === index,
                  },
                ]"
              >
                <AppIcon name="ArrowRightSmall" />
              </span>
            </span>
          </button>
          <button
            v-if="
              isShowSpreadButton &&
              !isShowFullText &&
              isLastItem(index) &&
              hasHiddenFullText(breadcrumb)
            "
            type="button"
            class="breadcrumbs__list-button _dots"
            aria-label="Показать полный текст"
            @click.prevent="showFullText"
          >
            ...
          </button>
        </template>
        <template v-else>
          <component
            :is="getBreadcrumbComponent(index)"
            :class="getBreadcrumbClasses(breadcrumb, index)"
            :to="breadcrumb?.to"
          >
            <template v-if="getBreadcrumbParts(breadcrumb, index)[0]">
              {{ getBreadcrumbParts(breadcrumb, index)[0] }}&nbsp;
            </template>
            <span class="breadcrumbs__nowrap">
              {{ getBreadcrumbParts(breadcrumb, index)[1] }}
              <span
                v-if="breadcrumb?.dropdownItems?.length && !isMobile"
                class="breadcrumbs__arrow"
                :class="{ _down: breadcrumb?.dropdownItems?.length }"
              >
                <AppIcon name="ArrowRightSmall" />
              </span>
            </span>
            <button
              v-if="
                isShowSpreadButton &&
                !isShowFullText &&
                isLastItem(index) &&
                hasHiddenFullText(breadcrumb)
              "
              type="button"
              class="breadcrumbs__list-button _dots"
              aria-label="Показать полный текст"
              @click.prevent="showFullText"
            >
              ...
            </button>
          </component>
        </template>

        <button
          v-if="
            isMobile &&
            breadcrumb?.dropdownItems?.length &&
            !(isLastItem(index) && breadcrumb?.dropdownItems?.length)
          "
          type="button"
          class="breadcrumbs__arrow-button"
          aria-label="Показать вложенный список"
          @mousedown.prevent
          @click.stop="toggleDropdown(index)"
        >
          <span
            :class="[
              'breadcrumbs__arrow _rotate',
              { _down: isShowDropdown && activeDropdownIndex === index },
            ]"
          >
            <AppIcon name="ArrowRightSmall" />
          </span>
        </button>

        <span
          v-if="!isLastItem(index) && !breadcrumb?.dropdownItems?.length"
          class="breadcrumbs__arrow"
        >
          <AppIcon name="ArrowRightSmall" />
        </span>

        <div
          v-if="breadcrumb?.dropdownItems?.length && activeDropdownIndex === index"
          v-show="isShowDropdown"
          :class="['breadcrumbs__dropdown', { _right: isRightSide }]"
          @mouseover.stop="!isMobile ? showDropdown(index) : null"
          @mouseleave="!isMobile ? hideDropdown() : null"
        >
          <AppBreadcrumbsDropdown
            :dropdown-title="breadcrumb?.dropdownTitle"
            :dropdown-items="breadcrumb?.dropdownItems"
          />
        </div>
      </li>
    </ol>
  </nav>
</template>

<style lang="less">
.breadcrumbs {
  margin: -4px 0;
  width: 100%;

  &__list-item {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;
    margin: 4px 0;
    position: relative;

    &:hover .breadcrumbs__arrow._down:not(._rotate) {
      transform: rotate(-90deg);
    }
  }

  &__list-link,
  &__list-text {
    &._down {
      cursor: pointer;
    }
  }

  &__list-link {
    .color-change-hover();
  }

  &__list-button {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;

    &._dots {
      color: @color-persimmon;
    }
  }

  &__list-item > &__list-button {
    margin-right: @main-size;
  }

  &__dropdown {
    position: absolute;
    top: @size-x5;
    padding-top: @size-x5;
    z-index: 10;

    &._right {
      right: -50px;
    }
  }

  &__nowrap {
    white-space: nowrap;
    display: inline;
  }

  &__arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: content-box;
    width: 6px;
    height: 14px;
    padding: 0 14px;
    vertical-align: middle;
    transition: transform @A ease-in-out;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  &__arrow._down {
    cursor: pointer;
    transform: rotate(90deg);
  }

  &__arrow._rotate {
    transform: rotate(90deg);
  }

  &__arrow._rotate._down {
    transform: rotate(-90deg);
  }

  &._mobile {
    position: relative;
  }

  &._mobile &__list {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x proximity;
    scrollbar-width: none;

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &._mobile &__list-item {
    position: static;
    white-space: nowrap;
    scroll-snap-align: start;
  }

  &._mobile &__dropdown {
    left: 0;
    top: @size-x6;
    padding-top: 0;
    width: 100%;
  }
}
</style>
